export const salesforceContent = `# Connect Your Salesforce to Rapider

This guide walks you through connecting your Salesforce account, selecting models, and scheduling sync — all on the same page in Rapider.

## Step 1: Enter Your Salesforce Credentials

You'll need three things:
- Username
- Password
- Security Token

These let Rapider securely connect to your Salesforce and get your data.

### Finding Your Username

Your username is usually the email you used to sign up for Salesforce.

**Where to find it:**
1. Log into Salesforce
2. Click your profile picture (top right)
3. Go to Settings → Personal Information → Username

### Finding Your Password

This is your regular Salesforce login password.

**New Salesforce account?** You'll need to set your password on first login. Make sure you complete this step before using Rapider.

### Getting Your Security Token

External apps like Rapider need a security token to access your Salesforce data.

**How to get it:**
1. Log into Salesforce
2. Click your profile picture (top right) → Settings
3. Go to My Personal Information → Reset My Security Token
4. Click "Reset Security Token"
5. Check your email - Salesforce will send the token within a few minutes
6. Copy the token and paste it into Rapider

### First-Time Salesforce Users

Just signed up for a new Salesforce Developer Account? Do this first:

1. Check your email for "Verify your account" or "Set your password" from Salesforce
2. Follow the link to create your password and finish setup
3. Log in to Salesforce once manually
4. Then get your security token using the steps above

*You must complete this initial Salesforce setup before connecting to Rapider.*

## Step 2: Pre-Selected Models for Faster Setup

To save you time, Rapider automatically pre-selects:
- The top 20 most-used Salesforce standard objects (e.g., Account, Contact, Opportunity, Lead, etc.)
- All custom objects unique to your Salesforce org

Why we do this:
- Salesforce has hundreds of standard objects — picking from scratch can be overwhelming
- Ensures you immediately see the most relevant data for faster integration
- Reduces confusion so you can take action without sorting through rarely used objects

You can always add or remove models before finalizing your selection.

## Step 3: Select Your Models

We'll automatically show you all your Salesforce objects:
- **Standard objects** like Account, Lead, Contact
- **Custom objects** your team created

Review the pre-selected list, then add or remove objects as needed.
These become live data models that update automatically, and you can adjust them later.

## Step 4: Set Your Sync Schedule

Choose how often we check for changes:
- Every 12 hours
- Every 24 hours

That's it - your Salesforce data will start syncing automatically!`;
